import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Shield, CheckCircle2, ArrowRight, PenLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { API } from "@/App";

function StarRating({ rating, interactive = false, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          data-testid={interactive ? `review-star-${i}` : undefined}
          size={interactive ? 28 : 14}
          className={`${interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''} ${
            i <= (interactive ? (hovered || rating) : rating)
              ? 'fill-amber-400 text-amber-400'
              : 'text-gray-200'
          }`}
          onMouseEnter={() => interactive && setHovered(i)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && onChange && onChange(i)}
        />
      ))}
    </div>
  );
}

function RatingBar({ label, count, total, onClick, active }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={`rating-bar-${label}`}
      className={`flex items-center gap-3 w-full group text-left ${active ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
    >
      <span className="text-xs font-medium text-muted-foreground w-6 shrink-0">{label}★</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${active ? 'bg-amber-400' : 'bg-amber-300 group-hover:bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
    </button>
  );
}

function ReviewCard({ review }) {
  const hasReply = !!review.reply_text;
  return (
    <Card data-testid={`review-card-${review.id}`} className="p-5 rounded-2xl border bg-background shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center text-primary text-sm font-bold shrink-0">
            {review.reviewer_name?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">{review.reviewer_name}</p>
              {review.verified_visit && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                  <CheckCircle2 size={9} /> Verified visit
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[10px] text-muted-foreground">
                {review.review_date}{review.review_time ? ` · ${review.review_time}` : ''}
              </span>
              {review.review_target_type === 'team_member' && review.team_member && (
                <span className="text-[10px] font-medium text-primary-foreground bg-primary rounded-full px-2 py-0.5">
                  {review.team_member}
                </span>
              )}
              {review.source_platform && review.source_platform !== 'bookd' && (
                <span className="text-[10px] font-medium text-foreground/50 border border-border bg-muted/30 rounded-full px-1.5 py-0.5 capitalize">via {review.source_platform}</span>
              )}
            </div>
          </div>
        </div>
        <StarRating rating={review.rating} />
      </div>
      <p className="text-sm text-foreground/80 leading-relaxed mb-3">&ldquo;{review.review_text}&rdquo;</p>
      {hasReply && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="bg-muted/40 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <span className="text-[9px] font-bold text-primary">{review.reply_author?.[0]?.toUpperCase() || 'S'}</span>
              </div>
              <span className="text-xs font-semibold">{review.reply_author || 'Salon'}</span>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {review.reply_date}{review.reply_time ? ` · ${review.reply_time}` : ''}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{review.reply_text}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function LeaveReviewForm({ onSuccess, staffList, businessMode }) {
  const [form, setForm] = useState({ name: '', email: '', rating: 0, text: '', target: 'salon', team_member: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const showTeamOption = businessMode === 'team' && staffList.length > 0;

  const submit = async () => {
    if (!form.name.trim()) return setError('Please enter your name.');
    if (form.rating === 0) return setError('Please select a star rating.');
    if (!form.text.trim()) return setError('Please write your review.');
    if (form.target === 'team_member' && !form.team_member) return setError('Please select a team member.');
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookd-reviews`, {
        reviewer_name: form.name,
        reviewer_email: form.email,
        rating: form.rating,
        review_text: form.text,
        review_target_type: form.target,
        team_member: form.target === 'team_member' ? form.team_member : '',
      });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-sm font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Your star rating</p>
        <div className="flex justify-center">
          <StarRating rating={form.rating} interactive onChange={r => setForm(f => ({ ...f, rating: r }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Your name *</label>
          <input
            data-testid="review-name-input"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Jane D"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email (optional)</label>
          <input
            data-testid="review-email-input"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            type="email"
            className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="jane@email.com"
          />
        </div>
      </div>
      {showTeamOption && (
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Review for</label>
          <div className="flex gap-3">
            {['salon', 'team_member'].map(t => (
              <button
                key={t}
                data-testid={`review-target-${t}`}
                onClick={() => setForm(f => ({ ...f, target: t, team_member: '' }))}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${
                  form.target === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover:bg-muted/50'
                }`}
              >
                {t === 'salon' ? 'The Salon' : 'Team Member'}
              </button>
            ))}
          </div>
        </div>
      )}
      {showTeamOption && form.target === 'team_member' && (
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Choose team member</label>
          <select
            data-testid="review-team-member-select"
            value={form.team_member}
            onChange={e => setForm(f => ({ ...f, team_member: e.target.value }))}
            className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">Select a team member...</option>
            {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Your review *</label>
        <textarea
          data-testid="review-text-input"
          value={form.text}
          onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
          className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
          rows={4}
          placeholder="Tell us about your experience..."
        />
      </div>
      {error && <p className="text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2" data-testid="review-error">{error}</p>}
      <Button data-testid="review-submit-btn" className="w-full rounded-xl h-11" onClick={submit} disabled={submitting}>
        {submitting ? 'Submitting...' : 'Submit Review'}
      </Button>
    </div>
  );
}

const PAGE_SIZE = 12;

export default function ReviewsPage() {
  const [summary, setSummary] = useState({ average_rating: 0, total: 0, breakdown: {} });
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterRating, setFilterRating] = useState(null);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [settings, setSettings] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const reviewMode = settings.review_mode || 'google';
  const directEnabled = reviewMode === 'direct' || reviewMode === 'both';
  const businessMode = settings.business_mode || (staffList.length > 1 ? 'team' : 'single');

  const loadReviews = useCallback(async () => {
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (filterRating) params.rating = filterRating;
      if (filterTeam !== 'all') params.team_member = filterTeam;
      if (filterTarget !== 'all') params.target_type = filterTarget;
      const res = await axios.get(`${API}/bookd-reviews`, { params });
      setReviews(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
  }, [page, filterRating, filterTeam, filterTarget]);

  useEffect(() => {
    const load = async () => {
      try {
        const [sumRes, staffRes, setRes] = await Promise.all([
          axios.get(`${API}/bookd-reviews/summary`),
          axios.get(`${API}/staff?active_only=true&slim=true`),
          axios.get(`${API}/settings?lite=true`),
        ]);
        setSummary(sumRes.data);
        setStaffList(staffRes.data || []);
        setSettings(setRes.data || {});
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const handleFilterChange = (setter) => (val) => {
    setPage(0);
    setter(val);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-background" data-testid="reviews-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-3">
            {settings.logo_url && (
              <img src={settings.logo_url} alt={settings.salon_name || 'Salon'} className="w-8 h-8 rounded-xl object-cover" />
            )}
            <span className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
              {settings.salon_name || 'Reviews'}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs rounded-xl" data-testid="reviews-back-btn">
                <ChevronLeft size={14} /> Back
              </Button>
            </Link>
            <Link to="/book">
              <Button size="sm" className="rounded-xl text-xs" data-testid="reviews-book-btn">Book Now</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero summary */}
        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 mb-3">
              <Shield size={14} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase">Verified Website Reviews</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Playfair Display' }}>
              Reviews you can trust
            </h1>
            <p className="text-sm text-muted-foreground mb-4 max-w-lg">
              All our ratings are from genuine customers, following verified visits.
            </p>
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="reviews-average">
                {summary.average_rating > 0 ? summary.average_rating.toFixed(1) : '—'}
              </span>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={20} className={i <= Math.round(summary.average_rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'} />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground" data-testid="reviews-total">Based on {summary.total} reviews</p>
              </div>
            </div>
            {directEnabled && (
              <Button
                data-testid="leave-review-btn"
                onClick={() => setShowForm(true)}
                className="rounded-xl gap-2"
                size="sm"
              >
                <PenLine size={14} /> Leave a Review
              </Button>
            )}
          </div>

          <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Rating breakdown</p>
            {[5, 4, 3, 2, 1].map(r => (
              <RatingBar
                key={r}
                label={r}
                count={summary.breakdown?.[r] || 0}
                total={summary.total}
                onClick={() => handleFilterChange(setFilterRating)(filterRating === r ? null : r)}
                active={filterRating === r}
              />
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-border">
          <span className="text-xs font-medium text-muted-foreground mr-1">Filter:</span>
          {[
            { val: 'all', label: 'All' },
            { val: 'salon', label: 'Salon' },
            ...(businessMode === 'team' ? [{ val: 'team_member', label: 'Team Member' }] : []),
          ].map(opt => (
            <button
              key={opt.val}
              data-testid={`filter-target-${opt.val}`}
              onClick={() => handleFilterChange(setFilterTarget)(opt.val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filterTarget === opt.val ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover:bg-muted/50'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <div className="w-px h-4 bg-border mx-1" />
          {[5, 4, 3, 2, 1].map(r => (
            <button
              key={r}
              data-testid={`filter-rating-${r}`}
              onClick={() => handleFilterChange(setFilterRating)(filterRating === r ? null : r)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filterRating === r ? 'bg-amber-400 text-white border-amber-400' : 'border-border bg-background hover:bg-muted/50'
              }`}
            >
              {r}<Star size={10} className="fill-current" />
            </button>
          ))}
          {businessMode === 'team' && staffList.length > 0 && (
            <>
              <div className="w-px h-4 bg-border mx-1" />
              <select
                data-testid="filter-team-member"
                value={filterTeam}
                onChange={e => handleFilterChange(setFilterTeam)(e.target.value)}
                className="border border-border rounded-full px-3 py-1.5 text-xs bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="all">All team</option>
                {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </>
          )}
          {(filterRating || filterTeam !== 'all' || filterTarget !== 'all') && (
            <button
              onClick={() => { setFilterRating(null); setFilterTeam('all'); setFilterTarget('all'); setPage(0); }}
              data-testid="filter-clear"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1"
            >
              <X size={12} /> Clear
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{total} review{total !== 1 ? 's' : ''}</span>
        </div>

        {reviews.length === 0 ? (
          <div className="text-center py-16" data-testid="reviews-empty">
            <Star size={32} className="text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No reviews found with these filters.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="reviews-prev-page">
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm text-muted-foreground px-2" data-testid="reviews-page-indicator">
              Page {page + 1} of {totalPages}
            </span>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="reviews-next-page">
              <ChevronRight size={16} />
            </Button>
          </div>
        )}

        <div className="mt-12 bg-gradient-to-r from-primary/8 via-primary/5 to-transparent rounded-2xl border border-primary/20 p-8 text-center">
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>Enjoyed your visit?</h2>
          <p className="text-sm text-muted-foreground mb-4">Share your experience and help others discover us.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {directEnabled && (
              <Button data-testid="leave-review-cta" className="rounded-xl gap-2" onClick={() => setShowForm(true)}>
                <PenLine size={14} /> Leave a Review
              </Button>
            )}
            <Link to="/book">
              <Button variant="outline" className="rounded-xl gap-2">
                Book your next visit <ArrowRight size={14} />
              </Button>
            </Link>
          </div>
        </div>

        {directEnabled && (
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogContent className="rounded-2xl max-w-md max-h-[90vh] overflow-y-auto">
              {submitted ? (
                <div className="text-center py-8" data-testid="review-success">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>Thank you!</h3>
                  <p className="text-sm text-muted-foreground mb-4">Your review has been submitted. We really appreciate you taking the time.</p>
                  <Button className="rounded-xl" onClick={() => { setShowForm(false); setSubmitted(false); loadReviews(); }}>
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>Leave a Review</DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground mt-1">We'd love to hear about your experience.</DialogDescription>
                  <div className="mb-5" />
                  <LeaveReviewForm staffList={staffList} businessMode={businessMode} onSuccess={() => setSubmitted(true)} />
                </>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
